import { useState, useEffect } from "react";
import { useIntl } from "react-intl";
import axios, { CancelToken } from "axios";
import {
  CARD_BALANCE,
  CARD_CANCEL,
  CARD_RESTORE_NIP,
  CARD_TRANSACTIONS,
} from "../../../../constants/api";

const makeRequest = async (url, source, method = "get", data = null) => {
  try {
    return await axios.request({
      url,
      method,
      data,
      cancelToken: source.token,
      headers: { "Content-Type": "application/json" },
    });
  } catch (error) {
    return error?.response;
  }
};

const cardDataMapper = (data) => {
  if (!data.result) {
    return undefined;
  }

  if (data.result.length) {
    const [unmappedCard] = data.result;
    const card = {
      timestamp: unmappedCard.card.activation_date,
      active: unmappedCard.card.active,
      currency: unmappedCard.balance.currency,
      funds: unmappedCard.balance.total_founds,
      cardNumber: unmappedCard.card.number,
      activationDate: unmappedCard.card.activation_date,
      cancelStatus: unmappedCard.card.cancelStatus,
    };
    return card;
  }

  return undefined;
};

const transactionDataMapper = (data, formatDate) => {
  if (!data.result) {
    return [];
  }
  const rows = data.result[0].transactions.map((item) => ({
    date: formatDate(item.timestamp, {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
    }),
    type: item.type,
    amount: item.total.amount,
    currency: item.total.currency,
  }));
  return rows;
};

const useCard = () => {
  const intl = useIntl();
  const [source, setSource] = useState();
  const [loading, setLoading] = useState(true);
  const [disabled, setDisabled] = useState(false);
  const [error] = useState();
  const [card, setCard] = useState();
  const [transactions, setTransactions] = useState([]);

  useEffect(() => {
    const initialRequest = async () => {
      setCard(cardDataMapper(await getCards()));
      setTransactions(
        transactionDataMapper(await getTransactions(), intl.formatDate)
      );
    };

    initialRequest();

    return () => source?.cancel("Component unmounted");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getCards = async () => {
    const sourceToken = CancelToken.source();
    setSource(sourceToken);
    setLoading(true);
    const result = await makeRequest(CARD_BALANCE, sourceToken);

    if (!result?.data.success) {
      setLoading(false);
      return [];
    }

    setLoading(false);
    return result?.data;
  };

  const getTransactions = async () => {
    const sourceToken = CancelToken.source();
    setSource(sourceToken);
    setLoading(true);
    const result = await makeRequest(CARD_TRANSACTIONS, sourceToken);

    if (!result?.data.success) {
      setLoading(false);
      return [];
    }

    setLoading(false);
    return result?.data;
  };

  const cancelCard = async (number) => {
    const sourceToken = CancelToken.source();
    setSource(sourceToken);
    setDisabled(true);
    const result = await makeRequest(CARD_CANCEL, sourceToken, "post", {
      number,
      reason: "OTBL",
    });
    setDisabled(false);
    if (result.status === 200) {
      setCard({ ...card, cancelStatus: 1 });
    }

    return result;
  };

  const sendNIP = async (number) => {
    const sourceToken = CancelToken.source();
    setSource(sourceToken);
    setDisabled(true);
    const result = await makeRequest(CARD_RESTORE_NIP, sourceToken, "post", {
      number,
    });
    setDisabled(false);
    return result;
  };

  return { loading, disabled, card, transactions, cancelCard, sendNIP, error };
};

export default useCard;
